import React, { useEffect, useReducer, useState, useCallback } from 'react';
import List from '@mui/icons-material/List';
import PropTypes from 'prop-types';
import { buildExportedRightsDownloadUrl } from '../../../../api';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { DOCUMENT_TYPE_WITH_STATE_TRANSITIONS_QUERY } from './queries';
import {
  SAVE_DOCUMENT_TYPE_WITH_STATE_TRANSITIONS,
  EXPORT_DOCUMENT_STATE_TRANSITION_RIGHTS_LIST,
  IMPORT_DOCUMENT_STATE_TRANSITION_RIGHTS
} from './mutations';
import { DOCUMENT_TYPE_WITH_STATE_TRANSITIONS_REMOVED_OR_UPDATED, DOCUMENT_STATE_TRANSITION_RIGHTS_IMPORTED } from './subscriptions';
import { Card } from '@totalsoft/rocket-ui';
import { Pagination } from '@totalsoft/rocket-ui';
import { FakeText } from '@totalsoft/rocket-ui';
import { useDebounce } from 'utils/customHooks';
import { reducer, initialState } from '../../../../reducers/stateTransitions/containerReducer';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useError } from '@ts-private/apollo-tools';
import { useToast } from 'hooks/toasts/useToast';
import { convertBase64ToFile } from 'utils/base64FileConversion';
import saveAs from 'file-saver';

import StateTransitionListFiltersComponent from './StateTransitionListFiltersComponent';
import StateTransitionListComponent from './StateTransitionListComponent';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { getOidcConfigName } from 'utils/functions';

const StateTransitionListContainer = props => {
  const { documentTypeId, onChangedDocumentTypeName, onEdit, onSaveFnChanged, onExportFnChanged, onImportFnChanged } = props;
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [listFilter, setListFilter] = useState({ transitionFilter: '', userGroupFilter: '' });
  const debouncedListFilter = useDebounce(listFilter, 150);
  const showError = useError();
  const addToast = useToast();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { accessToken } = useOidcAccessToken(getOidcConfigName());

  const onListFilterChanged = (prop, value) => setListFilter({ ...listFilter, [prop]: value });

  const { data: documentTypeWithStateTransitionsData, loading } = useQuery(DOCUMENT_TYPE_WITH_STATE_TRANSITIONS_QUERY, {
    onCompleted: data => {
      dispatch({
        type: 'OnInitDocumentTypeWithStateTransitions',
        documentTypeWithStateTransitions: {
          ...data.documentTypeWithStateTransitions,
          stateTransitions: data?.documentTypeWithStateTransitions?.stateTransitions.filter(
            x =>
              x.fromStateName?.toLowerCase().includes(listFilter.transitionFilter.toLowerCase()) ||
              x.toStateName?.toLowerCase().includes(listFilter.transitionFilter.toLowerCase())
          )
        }
      });
    },
    variables: {
      id: documentTypeId
    },
    notifyOnNetworkStatusChange: true
  });

  const { data: userGroupsData } = useQuery(USER_GROUPS_QUERY, {
    onCompleted: data => {
      dispatch({
        type: 'OnInitUserGroups',
        userGroups: data?.userGroupsConfig.filter(x => x.name.toLowerCase().includes(listFilter.userGroupFilter.toLowerCase()))
      });
    }
  });

  const [exportDocumentStateTransitionRights] = useMutation(EXPORT_DOCUMENT_STATE_TRANSITION_RIGHTS_LIST, {
    onCompleted: async ({ exportDocumentStateTransitionRights }) => {
      let downloadLink = null;
      try {
        if (exportDocumentStateTransitionRights.fileBytes) {
          saveAs(convertBase64ToFile(exportDocumentStateTransitionRights.fileBytes), exportDocumentStateTransitionRights.fileName);
          return;
        }
        downloadLink = await buildExportedRightsDownloadUrl(
          documentTypeId,
          exportDocumentStateTransitionRights.filePath,
          exportDocumentStateTransitionRights.fileName,
          accessToken
        );
      } catch (error) {
        showError(error);
      }
      if (downloadLink) {
        window.location.href = downloadLink;
      }
    }
  });

  const [importDocumentStateTransitionRights] = useMutation(IMPORT_DOCUMENT_STATE_TRANSITION_RIGHTS);

  const [saveDocumentTypeWithStateTransitions] = useMutation(SAVE_DOCUMENT_TYPE_WITH_STATE_TRANSITIONS, {
    onError: () => {
      dispatch({ type: 'OnSaveFailed' });
    }
  });

  useSubscription(DOCUMENT_TYPE_WITH_STATE_TRANSITIONS_REMOVED_OR_UPDATED, {
    onData: () => {
      addToast(t('DocumentTypes.DocumentWithStateTransitions.SavingSucceeded'), 'success');
      client.cache.evict({ id: `DocumentTypeWithStateTransitions:${documentTypeId}` });
      client.cache.gc();
    }
  });

  useSubscription(DOCUMENT_STATE_TRANSITION_RIGHTS_IMPORTED, {
    onData: _ => {
      addToast(t('DocumentTypes.DocumentWithStateTransitions.DocumentStateTransitionRightsImported'), 'success');
      client.cache.evict({ id: `DocumentTypeWithStateTransitions:${documentTypeId}` });
      client.cache.gc();
    }
  });

  const onPageChange = useCallback(page => {
    dispatch({ type: 'OnPageChange', page: page });
  }, []);

  const onRowsPerPageChange = useCallback(pageSize => {
    dispatch({ type: 'OnRowsPerPageChange', pageSize: pageSize });
  }, []);

  const onSave = useCallback(() => {
    dispatch({ type: 'OnSaving' });
    const changedStateTransitionsPermissions = Object.keys(localState.changedStateTransitionsPermissions).map(allowedTransitionId => {
      return {
        documentAllowedTransitionId: parseInt(allowedTransitionId),
        userGroupPermissions: Object.keys(localState.changedStateTransitionsPermissions[allowedTransitionId]).map(userGroupId => {
          return {
            userGroupId: parseInt(userGroupId),
            isAllowed: localState.changedStateTransitionsPermissions[allowedTransitionId][userGroupId]
          };
        })
      };
    });
    saveDocumentTypeWithStateTransitions({
      variables: {
        documentTypeId: localState.documentTypeId,
        allowedStateTransitions: changedStateTransitionsPermissions,
        deletedStateTransitions: localState.deletedStateTransitions
      }
    });
  }, [localState, saveDocumentTypeWithStateTransitions]);

  const onRemove = stateTransitionId => {
    dispatch({ type: 'OnRemoveStateTransition', stateTransitionId });
  };

  const onExport = useCallback(
    async fileType => {
      exportDocumentStateTransitionRights({ variables: { documentTypeId: documentTypeId, fileType: fileType } });
    },
    [exportDocumentStateTransitionRights, documentTypeId]
  );

  const onImport = useCallback(
    selectedFile => {
      if (!selectedFile) return;
      importDocumentStateTransitionRights({ variables: { documentTypeId: documentTypeId, file: selectedFile } });
    },
    [documentTypeId, importDocumentStateTransitionRights]
  );

  useEffect(() => {
    if (userGroupsData && userGroupsData.userGroupsConfig)
      dispatch({
        type: 'OnFilterList',
        documentTypeWithStateTransitions: {
          ...documentTypeWithStateTransitionsData?.documentTypeWithStateTransitions,
          stateTransitions: documentTypeWithStateTransitionsData?.documentTypeWithStateTransitions?.stateTransitions.filter(
            x =>
              x.fromStateName?.toLowerCase().includes(listFilter.transitionFilter.toLowerCase()) ||
              x.toStateName?.toLowerCase().includes(listFilter.transitionFilter.toLowerCase())
          )
        },
        userGroups: userGroupsData?.userGroupsConfig.filter(x => x.name.toLowerCase().includes(listFilter.userGroupFilter.toLowerCase()))
      });
  }, [documentTypeWithStateTransitionsData, userGroupsData, listFilter, debouncedListFilter]);

  useEffect(() => {
    if (documentTypeWithStateTransitionsData?.documentTypeWithStateTransitions)
      onChangedDocumentTypeName(documentTypeWithStateTransitionsData?.documentTypeWithStateTransitions.name);
  }, [onChangedDocumentTypeName, documentTypeWithStateTransitionsData]);

  useEffect(() => {
    onSaveFnChanged(onSave, localState.allowSave);
  }, [onSaveFnChanged, onSave, localState.allowSave]);

  useEffect(() => {
    onImportFnChanged(onImport);
  }, [onImportFnChanged, onImport]);

  useEffect(() => {
    onExportFnChanged(onExport);
  }, [onExportFnChanged, onExport, localState.disabledExport]);

  return (
    <Card icon={List} iconColor='primary'>
      {loading ? (
        <FakeText lines={localState.pager.pageSize} />
      ) : userGroupsData && userGroupsData.userGroupsConfig ? (
        <>
          <StateTransitionListFiltersComponent listFilter={listFilter} onListFilterChanged={onListFilterChanged} />
          <StateTransitionListComponent
            changed={localState.allowSave}
            localState={localState}
            onEdit={onEdit}
            onSave={onSave}
            onRemove={onRemove}
            dispatch={dispatch}
          />
          <Pagination
            loading={loading}
            count={localState.documentTypeWithStateTransitions.stateTransitions?.length ?? 0}
            pageSize={localState.pager.pageSize}
            page={localState.pager.page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </>
      ) : (
        <></>
      )}
    </Card>
  );
};

StateTransitionListContainer.propTypes = {
  documentTypeId: PropTypes.number.isRequired,
  onChangedDocumentTypeName: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired,
  onExportFnChanged: PropTypes.func.isRequired,
  onImportFnChanged: PropTypes.func.isRequired
};

export default StateTransitionListContainer;
